import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "@/lib/theme";
import PdfCanvas from "@/components/PdfCanvas";
import VideoCanvas from "@/components/VideoCanvas";
import { Lock, ShieldCheck } from "lucide-react";

/**
 * EmbedReview — minimal iframe-friendly view of a review link.
 * No app chrome, no sidebar, no comment panel. Branded with workspace white-label.
 * Route: /embed/r/:token
 */
export default function EmbedReview() {
  const { token } = useParams();
  const { branding } = useTheme();
  const [meta, setMeta] = useState(null);
  const [wsBrand, setWsBrand] = useState(null);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/r/${token}`).then((r) => {
      setMeta(r.data);
      if (r.data.requires_password) {
        setError("password_required");
      } else {
        api.post(`/r/${token}/load`, { password: "" }).then((p) => setPayload(p.data)).catch(() => setError("load_failed"));
      }
    }).catch(() => setError("not_found"));
    api.get(`/r/${token}/branding`).then((r) => setWsBrand(r.data)).catch(() => {});
    api.post(`/r/${token}/track-open`).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (wsBrand?.white_label && wsBrand?.primary_hsl) {
      const root = document.documentElement;
      const prev = root.style.getPropertyValue("--primary");
      root.style.setProperty("--primary", wsBrand.primary_hsl);
      return () => { root.style.setProperty("--primary", prev); };
    }
  }, [wsBrand]);

  if (error === "not_found") {
    return <Centered>Review link not found or expired.</Centered>;
  }
  if (error === "password_required") {
    return (
      <Centered>
        <Lock className="w-6 h-6 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm">This review is password-protected.</p>
        <a className="text-primary text-xs hover:underline mt-2 inline-block" href={`/r/${token}`} target="_blank" rel="noopener noreferrer" data-testid="embed-open-secure-link">Open the full link →</a>
      </Centered>
    );
  }
  if (error === "load_failed") {
    return <Centered>Failed to load review.</Centered>;
  }
  if (!meta || !payload) {
    return <Centered>Loading review…</Centered>;
  }

  const { item, version, comments } = payload;
  const displayName = wsBrand?.white_label ? (wsBrand.workspace_name || branding.platform_name) : branding.platform_name;
  const displayLogo = wsBrand?.white_label ? wsBrand.logo_url : null;
  const accentText = wsBrand?.white_label ? wsBrand.accent_text : null;

  return (
    <div className="min-h-screen flex flex-col bg-background" data-theme="canvas" data-testid="embed-root">
      <header className="flex items-center justify-between px-4 py-2 border-b border-border bg-card">
        <div className="flex items-center gap-2 min-w-0">
          {displayLogo ? (
            <img src={displayLogo} alt={displayName} className="w-7 h-7 object-contain" />
          ) : (
            <div className="w-7 h-7 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-black text-sm">
              {(displayName || "C").slice(0, 1)}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-xs font-semibold truncate" style={{ fontFamily: "var(--font-heading)" }} data-testid="embed-item-title">{item.title}</div>
            <div className="text-[10px] text-muted-foreground">v{version.version_number} · {item.item_type}</div>
          </div>
        </div>
        <a
          href={`/r/${token}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs label-eyebrow hover:text-primary inline-flex items-center"
          data-testid="embed-open-full-link"
        >
          Open <ShieldCheck className="w-3 h-3 ml-1" />
        </a>
      </header>

      <div className="flex-1 overflow-auto bg-muted/40 flex items-start justify-center p-3">
        {item.item_type === "image" && version?.file_data_url && (
          <div className="relative" style={{ width: "min(100%, 900px)" }} data-testid="embed-image">
            <img src={version.file_data_url} alt={item.title} className="w-full h-auto block" draggable={false} />
            {comments.map((c, i) => {
              if (!c.annotation || c.annotation.type !== "pin") return null;
              return (
                <div key={c.id} className={`anno-pin ${c.status === "resolved" ? "resolved" : ""}`}
                  style={{ left: `${c.annotation.x}%`, top: `${c.annotation.y}%` }}>
                  {i + 1}
                </div>
              );
            })}
          </div>
        )}
        {item.item_type === "pdf" && version?.file_data_url && (
          <PdfCanvas
            fileUrl={version.file_data_url}
            comments={comments}
            readOnly
            activeTool="pin"
            onCreateAnnotation={() => {}}
            selectedCommentId={null}
            setSelectedCommentId={() => {}}
            testidPrefix="embed-anno"
          />
        )}
        {item.item_type === "video" && version?.file_data_url && (
          <VideoCanvas
            fileUrl={version.file_data_url}
            comments={comments}
            readOnly
            activeTool="pin"
            onCreateAnnotation={() => {}}
            selectedCommentId={null}
            setSelectedCommentId={() => {}}
            testidPrefix="embed-anno"
          />
        )}
      </div>

      <footer className="text-center text-[10px] text-foreground/60 py-2 border-t border-border bg-card" data-testid="embed-footer">
        {accentText || `Review on ${displayName}`}
      </footer>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen grid place-items-center text-center text-sm text-muted-foreground p-6" data-testid="embed-state">
      <div>{children}</div>
    </div>
  );
}
